import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, CheckCircle, ArrowRight } from 'lucide-react'
import { supabase } from '../lib/supabase'

export const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [isValidToken, setIsValidToken] = useState(false)

  useEffect(() => {
    // Check if we have a valid recovery token
    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidToken(true)
      }
    })

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setIsValidToken(true)
      }
    })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    // Validate password strength
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setIsLoading(true)

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })

    if (error) {
      setError(error.message)
      setIsLoading(false)
    } else {
      setIsSuccess(true)
      setIsLoading(false)
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/')
      }, 3000)
    }
  }

  if (isSuccess) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '24px'
      }}>
        <div style={{ width: '100%', maxWidth: '512px' }}>
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            backdropFilter: 'blur(8px)', 
            borderRadius: '24px', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            border: '1px solid rgba(255, 255, 255, 0.2)', 
            overflow: 'hidden'
          }}>
            {/* Success Header */}
            <div style={{ 
              background: 'linear-gradient(to right, #10b981, #059669)', 
              padding: '32px', 
              textAlign: 'center'
            }}>
              <div style={{ 
                margin: '0 auto 16px', 
                height: '64px', 
                width: '64px', 
                background: 'rgba(255, 255, 255, 0.2)', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center'
              }}>
                <CheckCircle style={{ height: '32px', width: '32px', color: 'white' }} />
              </div>
              <h2 style={{ 
                fontSize: '30px', 
                fontWeight: 'bold', 
                color: 'white', 
                marginBottom: '8px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Password Updated
              </h2>
              <p style={{ 
                color: '#d1fae5', 
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Your password has been successfully reset
              </p>
            </div>

            {/* Success Body */}
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <p style={{ 
                color: '#6b7280', 
                marginBottom: '24px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Redirecting you to the login page...
              </p>
              
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                animation: 'spin 1s linear infinite', 
                borderRadius: '50%', 
                height: '32px', 
                width: '32px', 
                border: '2px solid #10b981', 
                borderBottomColor: 'transparent'
              }}></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!isValidToken) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: '24px'
      }}>
        <div style={{ width: '100%', maxWidth: '512px' }}>
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            backdropFilter: 'blur(8px)', 
            borderRadius: '24px', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            border: '1px solid rgba(255, 255, 255, 0.2)', 
            padding: '32px',
            textAlign: 'center'
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '16px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              Invalid or Expired Link
            </h2>
            <p style={{ 
              color: '#6b7280', 
              marginBottom: '24px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '24px'
    }}>
      <div style={{ width: '100%', maxWidth: '512px' }}>
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.95)', 
          backdropFilter: 'blur(8px)', 
          borderRadius: '24px', 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
          border: '1px solid rgba(255, 255, 255, 0.2)', 
          overflow: 'hidden'
        }}>
          {/* Header Section */}
          <div style={{ 
            background: 'linear-gradient(to right, #2563eb, #4f46e5)', 
            padding: '32px', 
            textAlign: 'center'
          }}>
            <div style={{ 
              margin: '0 auto 16px', 
              height: '64px', 
              width: '64px', 
              background: 'rgba(255, 255, 255, 0.2)', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}>
              <Lock style={{ height: '32px', width: '32px', color: 'white' }} />
            </div>
            <h1 style={{ 
              fontSize: '30px', 
              fontWeight: 'bold', 
              color: 'white', 
              marginBottom: '8px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              Set New Password
            </h1>
            <p style={{ 
              color: '#bfdbfe', 
              fontWeight: '500',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              Choose a strong password for your account
            </p>
          </div>

          {/* Form Section */}
          <div style={{ padding: '32px' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <label htmlFor="new-password" style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '12px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="new-password"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '48px',
                      paddingRight: '16px',
                      paddingTop: '16px',
                      paddingBottom: '16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    placeholder="Enter new password"
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <Lock style={{ 
                    position: 'absolute', 
                    left: '16px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    height: '20px', 
                    width: '20px', 
                    color: '#9ca3af'
                  }} />
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '12px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="confirm-password"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '48px',
                      paddingRight: '16px',
                      paddingTop: '16px',
                      paddingBottom: '16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    placeholder="Confirm new password"
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <Lock style={{ 
                    position: 'absolute', 
                    left: '16px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    height: '20px', 
                    width: '20px', 
                    color: '#9ca3af'
                  }} />
                </div>
              </div>

              {error && (
                <div style={{ 
                  borderRadius: '12px', 
                  padding: '16px', 
                  background: '#fef2f2', 
                  border: '1px solid #fecaca'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flexShrink: 0 }}>
                      <div style={{ height: '20px', width: '20px', color: '#f87171' }}>
                        <svg viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div style={{ marginLeft: '12px' }}>
                      <p style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#dc2626',
                        fontFamily: 'Lexend Deca, sans-serif'
                      }}>{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                  color: 'white',
                  fontWeight: '600',
                  borderRadius: '12px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  transition: 'all 0.2s',
                  opacity: isLoading ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    const target = e.target as HTMLButtonElement;
                    target.style.background = 'linear-gradient(to right, #1d4ed8, #4338ca)';
                    target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    const target = e.target as HTMLButtonElement;
                    target.style.background = 'linear-gradient(to right, #2563eb, #4f46e5)';
                    target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                  }
                }}
              >
                {isLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ 
                      animation: 'spin 1s linear infinite', 
                      borderRadius: '50%', 
                      height: '20px', 
                      width: '20px', 
                      border: '2px solid white', 
                      borderBottomColor: 'transparent', 
                      marginRight: '8px'
                    }}></div>
                    Updating password...
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Update Password
                    <ArrowRight style={{ marginLeft: '8px', height: '20px', width: '20px' }} />
                  </div>
                )}
              </button>
            </form>

            {/* Password Requirements */}
            <div style={{ 
              marginTop: '24px', 
              paddingTop: '24px', 
              borderTop: '1px solid #e5e7eb'
            }}>
              <p style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                fontFamily: 'Lexend Deca, sans-serif',
                marginBottom: '8px'
              }}>
                Password requirements:
              </p>
              <ul style={{ 
                fontSize: '12px', 
                color: '#9ca3af',
                fontFamily: 'Lexend Deca, sans-serif',
                paddingLeft: '20px'
              }}>
                <li>At least 6 characters long</li>
                <li>Must match the confirmation password</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}